import { DemandPlan } from "./DemandPlan";
import { OrderWindow } from "./OrderWindow";
import { Variant } from "./Variant";

export type DemandPlanVariant = {
  id: string;
  status: DemandPlanVariantStatus;
  inventoryVelocity: number;
  leadTimeDays: number;
  leadTimeInventory: number;
  leadTimeSafetyStock: number;
  preorderQuantity: number;
  demandCoverageDays: number;
  demandCoverageInventory: number;
  demandCoverageSafetyStock: number;
  totalDemand: number;
  recommendedToOrder: number;
  toOrder: number;

  demandMeetsMoq: boolean;
  inventoryMeetsDemand: boolean;

  demandPlan: DemandPlan;
  variant: Variant;
  orderWindows: OrderWindow[];

  supplierName: string;
};

type FieldMeta = {
  label: string;
};
type StatusMeta = FieldMeta & {
  className?:
    | "default"
    | "primary"
    | "secondary"
    | "error"
    | "info"
    | "success"
    | "warning"
    | undefined;
};

export enum DemandPlanVariantStatus {
  Draft = "draft",
  InProgress = "in-progress",
  PendingReview = "pending-review",
  ReadyForPurchase = "ready-for-purchase",
  Terminated = "terminated",
}

export const DemandPlanVariantStatusMeta: Map<
  DemandPlanVariantStatus,
  StatusMeta
> = new Map<DemandPlanVariantStatus, StatusMeta>([
  [DemandPlanVariantStatus.Draft, { label: "Draft" }],
  [
    DemandPlanVariantStatus.InProgress,
    { label: "In-Progress", className: "primary" },
  ],
  [
    DemandPlanVariantStatus.PendingReview,
    { label: "Pending Review", className: "warning" },
  ],
  [
    DemandPlanVariantStatus.ReadyForPurchase,
    { label: "Ready for Purchase", className: "success" },
  ],
  [
    DemandPlanVariantStatus.Terminated,
    { label: "Terminated", className: "error" },
  ],
]);

const DemandPlanVariantStatusWorkflow: Map<
  DemandPlanVariantStatus,
  DemandPlanVariantStatus[]
> = new Map<DemandPlanVariantStatus, DemandPlanVariantStatus[]>([
  [
    DemandPlanVariantStatus.Draft,
    [
      DemandPlanVariantStatus.PendingReview,
      DemandPlanVariantStatus.ReadyForPurchase,
      DemandPlanVariantStatus.Terminated,
    ],
  ],
  [
    DemandPlanVariantStatus.InProgress,
    [
      DemandPlanVariantStatus.PendingReview,
      DemandPlanVariantStatus.ReadyForPurchase,
      DemandPlanVariantStatus.Terminated,
    ],
  ],
  [
    DemandPlanVariantStatus.PendingReview,
    [
      DemandPlanVariantStatus.ReadyForPurchase,
      DemandPlanVariantStatus.Terminated,
    ],
  ],
  [DemandPlanVariantStatus.ReadyForPurchase, []],
  [DemandPlanVariantStatus.Terminated, []],
]);

export const canTransition = (
  record: DemandPlanVariant,
  nextStatus: DemandPlanVariantStatus
): boolean => {
  return !!DemandPlanVariantStatusWorkflow.get(record.status)?.includes(
    nextStatus
  );
};
